import { useEffect, useMemo, useRef, useState } from 'react';
import { Minus, PackagePlus, Plus, Search, X } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet';
import { cn } from '@/shared/lib/cn';
import type { Product } from '@/shared/types/product';
import type { OperatorSaleDetail } from '../model/operator-payload';

interface OperatorProductPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: Product[];
  loading?: boolean;
  saleDetails: OperatorSaleDetail[];
  onSaleDetailChange: (sku: string, name: string, count: number) => void;
}

export function normalizeQuery(q: string): string {
  return q
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function matchesProduct(product: Product, q: string): boolean {
  if (!q) return true;
  const name = normalizeQuery(product.name ?? '');
  const sku = normalizeQuery(product.uniqueId ?? '');
  return name.includes(q) || sku.includes(q);
}

export function OperatorProductPicker({
  open,
  onOpenChange,
  products,
  loading,
  saleDetails,
  onSaleDetailChange,
}: OperatorProductPickerProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const qtyBySku = useMemo(() => new Map(saleDetails.map((d) => [d.sku, d.count])), [saleDetails]);
  const selectedCount = saleDetails.reduce((n, d) => n + d.count, 0);
  const normalized = normalizeQuery(query);

  const filtered = useMemo(() => {
    const list = products.filter((p) => matchesProduct(p, normalized));
    // Selecionados primeiro, depois nome
    return list.sort((a, b) => {
      const qa = qtyBySku.get(a.uniqueId) ?? 0;
      const qb = qtyBySku.get(b.uniqueId) ?? 0;
      if (qa > 0 && qb === 0) return -1;
      if (qb > 0 && qa === 0) return 1;
      return (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR');
    });
  }, [products, normalized, qtyBySku]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
    return () => window.clearTimeout(t);
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <SheetHeader className="mb-0 shrink-0 space-y-1 border-b border-[hsl(var(--glass-border)/0.4)] px-5 py-4 text-left">
          <SheetTitle>Adicionar produtos</SheetTitle>
          <SheetDescription>Busque por nome ou SKU e ajuste a quantidade.</SheetDescription>
        </SheetHeader>

        <div className="shrink-0 border-b border-[hsl(var(--glass-border)/0.35)] px-4 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nome ou SKU…"
              className="h-11 rounded-2xl pl-10 pr-10 text-base"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {query ? (
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                aria-label="Limpar busca"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-3 py-2">
          {loading ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">Carregando produtos…</p>
          ) : products.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              Nenhum produto cadastrado nesta loja.
            </p>
          ) : filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              Nenhum resultado para “{query.trim()}”.
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((p) => {
                const sku = p.uniqueId;
                const count = qtyBySku.get(sku) ?? 0;
                const selected = count > 0;
                return (
                  <li
                    key={p._id || sku}
                    className={cn(
                      'flex items-center gap-2 rounded-xl px-2.5 py-2.5',
                      selected ? 'bg-primary/10' : 'hover:bg-accent/50',
                    )}
                  >
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => onSaleDetailChange(sku, p.name, selected ? count + 1 : 1)}
                    >
                      <p className="truncate text-sm font-medium">{p.name}</p>
                      <p className="truncate text-xs text-muted-foreground">SKU {sku}</p>
                    </button>
                    {selected ? (
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          type="button"
                          variant="secondary"
                          size="icon"
                          className="size-11"
                          onClick={() => onSaleDetailChange(sku, p.name, Math.max(0, count - 1))}
                        >
                          <Minus className="size-3.5" />
                        </Button>
                        <span className="w-7 text-center text-sm tabular-nums font-semibold">{count}</span>
                        <Button
                          type="button"
                          variant="secondary"
                          size="icon"
                          className="size-11"
                          onClick={() => onSaleDetailChange(sku, p.name, count + 1)}
                        >
                          <Plus className="size-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon"
                        className="size-11 shrink-0"
                        onClick={() => onSaleDetailChange(sku, p.name, 1)}
                        aria-label={`Adicionar ${p.name}`}
                      >
                        <Plus className="size-4" />
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="shrink-0 border-t border-[hsl(var(--glass-border)/0.4)] px-4 py-3">
          <Button type="button" className="h-11 w-full" onClick={() => onOpenChange(false)}>
            {selectedCount > 0
              ? `Pronto · ${selectedCount} ${selectedCount === 1 ? 'item' : 'itens'}`
              : 'Fechar'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface OperatorSaleLinesProps {
  saleDetails: OperatorSaleDetail[];
  onSaleDetailChange: (sku: string, name: string, count: number) => void;
  onAddClick: () => void;
  loading?: boolean;
  emptyCatalog?: boolean;
}

/** Resumo compacto na Conta + CTA para abrir o picker. */
export function OperatorSaleLines({
  saleDetails,
  onSaleDetailChange,
  onAddClick,
  loading,
  emptyCatalog,
}: OperatorSaleLinesProps) {
  const totalItems = saleDetails.reduce((n, d) => n + d.count, 0);
  const lines = [...saleDetails].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Itens da compra</p>
          <p className="text-xs text-muted-foreground">
            {totalItems > 0
              ? `${totalItems} ${totalItems === 1 ? 'unidade' : 'unidades'} · ${lines.length} ${lines.length === 1 ? 'produto' : 'produtos'}`
              : 'Nenhum item ainda'}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-10 gap-1.5"
          onClick={onAddClick}
          disabled={loading || emptyCatalog}
        >
          <PackagePlus className="size-4" />
          {totalItems > 0 ? 'Adicionar' : 'Buscar produtos'}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Carregando catálogo…</p>
      ) : emptyCatalog ? (
        <p className="text-sm text-muted-foreground">Nenhum produto cadastrado nesta loja.</p>
      ) : lines.length === 0 ? (
        <button
          type="button"
          onClick={onAddClick}
          className="flex w-full flex-col items-center gap-2 rounded-2xl border border-dashed border-[hsl(var(--glass-border)/0.55)] px-4 py-8 text-center transition-colors hover:border-primary/40 hover:bg-primary/5"
        >
          <PackagePlus className="size-6 text-muted-foreground" />
          <span className="text-sm font-medium">Buscar e adicionar produtos</span>
          <span className="text-xs text-muted-foreground">Nome ou SKU — rápido no balcão</span>
        </button>
      ) : (
        <ul className="space-y-1 rounded-2xl border border-[hsl(var(--glass-border)/0.45)] p-1.5">
          {lines.map((d) => (
            <li
              key={d.sku}
              className="flex items-center gap-2 rounded-xl bg-primary/5 px-2.5 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{d.name}</p>
                <p className="truncate text-xs text-muted-foreground">SKU {d.sku}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="size-10"
                  onClick={() => onSaleDetailChange(d.sku, d.name, Math.max(0, d.count - 1))}
                >
                  <Minus className="size-3.5" />
                </Button>
                <span className="w-6 text-center text-sm tabular-nums font-semibold">{d.count}</span>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="size-10"
                  onClick={() => onSaleDetailChange(d.sku, d.name, d.count + 1)}
                >
                  <Plus className="size-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
